/**
 * The shell (doc 11 §Screen map, doc 18 §5). One canvas, one set of DOM
 * listeners, one UI pixel buffer — and exactly one live screen, which the shell
 * swaps: TITLE → SLOTS → DEPTH SELECT → RUN → RESULTS, with COLONY COMMAND
 * hanging off the title.
 *
 * The shell also owns the save file: screens mutate `app.profile` and call
 * `app.persist()`, and every Pearl is on disk the moment it is earned (doc 08).
 */

import { Container } from 'pixi.js';
import { audio } from '../audio/engine.ts';
import { modifiersOf } from '../meta/effects.ts';
import { applyPressure } from '../meta/pressure.ts';
import type { Profile } from '../meta/types.ts';
import { uiScaleFor, type UiScale } from '../render/camera.ts';
import { createLayers, DynamicSurface, TextureCache, type Layers } from '../render/pipeline.ts';
import { PixelSurface } from '../render/surface.ts';
import { browserStorage, SaveStore, type SlotId } from '../save/store.ts';
import { DOUBLE_REEL_BONUS } from '../sim/const.ts';
import { NEUTRAL_MODIFIERS } from '../sim/defs.ts';
import type { RunModifiers } from '../sim/types.ts';

export interface Screen {
  /** Per frame; `now` is `performance.now()`. */
  update(now: number): void;
  destroy(): void;
  resize?(): void;
  onKey?(e: KeyboardEvent): void;
  onKeyUp?(e: KeyboardEvent): void;
  onMouseDown?(e: MouseEvent): void;
  onMouseUp?(e: MouseEvent): void;
  onMouseMove?(e: MouseEvent): void;
  onWheel?(e: WheelEvent): void;
  onBlur?(): void;
}

export type ScreenFactory = (app: App) => Screen;

export class App {
  readonly layers: Layers;
  readonly canvas: HTMLCanvasElement;
  readonly cache = new TextureCache();
  readonly store: SaveStore;
  /** Everything a screen draws into; recreated on resize. */
  hud: DynamicSurface;
  uiScale: UiScale;
  viewport = { cssW: window.innerWidth, cssH: window.innerHeight };
  /** The screen's own world-space container, cleared on every swap. */
  readonly stage = new Container();

  slot: SlotId | null = null;
  profile: Profile | null = null;

  private screen: Screen | null = null;
  private lastSaveError: string | null = null;

  constructor(canvas: HTMLCanvasElement, layers: Layers) {
    this.canvas = canvas;
    this.layers = layers;
    this.store = new SaveStore(browserStorage());
    this.uiScale = uiScaleFor(this.viewport.cssH);
    this.hud = new DynamicSurface(
      Math.ceil(this.viewport.cssW / this.uiScale),
      Math.ceil(this.viewport.cssH / this.uiScale),
    );
    layers.world.addChild(this.stage);
    layers.ui.addChild(this.hud.sprite);
    layers.ui.scale.set(this.uiScale);
    this.wire();
  }

  get surface(): PixelSurface {
    return this.hud.surface;
  }

  /** UI-space pointer coordinates for a mouse event. */
  ui(e: MouseEvent): { x: number; y: number } {
    return { x: Math.floor(e.offsetX / this.uiScale), y: Math.floor(e.offsetY / this.uiScale) };
  }

  go(make: ScreenFactory): void {
    this.screen?.destroy();
    this.stage.removeChildren();
    this.hud.surface.clear();
    this.screen = make(this);
  }

  get current(): Screen | null {
    return this.screen;
  }

  // -- the save file ---------------------------------------------------------

  useProfile(slot: SlotId, profile: Profile): void {
    this.slot = slot;
    this.profile = profile;
    this.resize(); // this colony may want a different UI scale than the last one
    this.applyVolumes();
  }

  /** Push the profile's mixer into the engine (doc 18 §1). */
  applyVolumes(): void {
    const s = this.profile?.settings;
    if (!s) return;
    audio.setVolumes({
      master: s.volMaster,
      music: s.volMusic,
      sfx: s.volSfx,
      ambience: s.volAmbience,
    });
  }

  /** Write the profile to its slot. Failure is reported, never thrown. */
  persist(): string | null {
    if (!this.slot || !this.profile) return null;
    this.lastSaveError = this.store.save(this.slot, this.profile);
    return this.lastSaveError;
  }

  get saveError(): string | null {
    return this.lastSaveError;
  }

  /**
   * The run's numbers for the current profile — neutral when there is none.
   * Pressure folds in last (doc 08): it is a per-DEPTH choice, so the depth
   * the run is about has to be named.
   *
   * The auto-lure SETTING rides the same field Pressure's RUSH HOUR uses, and
   * whichever is shorter wins — so the two can never disagree about when the
   * rim stops waiting (doc 18 §2).
   */
  modifiers(depth = 1): RunModifiers {
    if (!this.profile) return NEUTRAL_MODIFIERS;
    const base = modifiersOf(this.profile, DOUBLE_REEL_BONUS);
    const chosen = this.profile.settings.autoLureSeconds;
    if (chosen > 0) {
      base.autoLureSeconds =
        base.autoLureSeconds > 0 ? Math.min(base.autoLureSeconds, chosen) : chosen;
    }
    return applyPressure(base, this.profile.pressure[String(depth)] ?? []);
  }

  /**
   * Doc 18 §3: AUTO honours the OS. Everything that fades, slides, shakes or
   * blinks asks this one question rather than each reading the media query.
   */
  get reducedMotion(): boolean {
    const pref = this.profile?.settings.reducedMotion ?? 'auto';
    if (pref !== 'auto') return pref === 'on';
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Re-derive the UI scale after the setting changed, and repaint into it. */
  applyUiScale(): void {
    this.resize();
  }

  // -- input, once, forwarded to whoever is on screen -------------------------

  private wire(): void {
    const c = this.canvas;
    c.addEventListener('contextmenu', (e) => e.preventDefault());
    // Doc 18 §1: WebAudio does not exist until a gesture says so. Every gesture
    // calls it, because the browser may suspend the context again at any point.
    const unlock = (): void => {
      audio.unlock();
      this.applyVolumes();
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    c.addEventListener('mousedown', (e) => this.screen?.onMouseDown?.(e));
    c.addEventListener('mousemove', (e) => this.screen?.onMouseMove?.(e));
    c.addEventListener('wheel', (e) => this.screen?.onWheel?.(e), { passive: false });
    window.addEventListener('mouseup', (e) => this.screen?.onMouseUp?.(e));
    window.addEventListener('keydown', (e) => this.screen?.onKey?.(e));
    window.addEventListener('keyup', (e) => this.screen?.onKeyUp?.(e));
    window.addEventListener('blur', () => {
      audio.setMuted(true); // doc 18 §1, paired with the auto-pause (doc 17 §5)
      this.screen?.onBlur?.();
    });
    window.addEventListener('focus', () => audio.setMuted(false));
    // doc 17 §5 / doc 18 §6: a hidden tab pauses, which also sidesteps the
    // background rAF throttling that would otherwise stutter the resume
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        audio.setMuted(true);
        this.screen?.onBlur?.();
      } else {
        audio.setMuted(false);
      }
    });
    // doc 17 §2: ctrl+wheel is browser zoom, and browser zoom over a
    // pixel-perfect canvas is a blurry mess
    c.addEventListener(
      'wheel',
      (e) => {
        if (e.ctrlKey) e.preventDefault();
      },
      { passive: false },
    );
    // doc 18 §6: WebGL contexts are lost on GPU pressure and tab suspension.
    // The renderer rebuilds; the run is already on disk at its last payday.
    c.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      void this.persist();
      audio.setMuted(true);
      this.screen?.onBlur?.();
    });
    c.addEventListener('webglcontextrestored', () => {
      this.cache.clear();
      audio.setMuted(false);
      this.resize(); // rebuild every surface at the current size, then repaint
    });
    window.addEventListener('beforeunload', () => void this.persist());
    window.addEventListener('resize', () => this.resize());
    this.layers.app.ticker.add(() => this.screen?.update(performance.now()));
  }

  private resize(): void {
    this.viewport.cssW = window.innerWidth;
    this.viewport.cssH = window.innerHeight;
    this.uiScale = uiScaleFor(this.viewport.cssH, this.profile?.settings.uiScale ?? 'auto');
    this.hud.resize(
      Math.ceil(this.viewport.cssW / this.uiScale),
      Math.ceil(this.viewport.cssH / this.uiScale),
    );
    this.layers.ui.scale.set(this.uiScale);
    this.screen?.resize?.();
  }
}

export async function createApp(canvas: HTMLCanvasElement): Promise<App> {
  const layers = await createLayers(canvas, 0x020508);
  return new App(canvas, layers);
}
